import {
  Server,
  Database,
  HardDrive,
  Cloud,
  Box,
  Package,
  Layers,
  Network,
  Router,
  Globe,
  Shield,
  ShieldCheck,
  Lock,
  Key,
  Activity,
  Zap,
  Container,
  Boxes,
  Workflow,
  GitBranch,
  FileText,
  Folder,
  Archive,
  Settings,
  Terminal,
  Inbox,
  Waypoints,
  Server as ServerCluster,
  type LucideIcon,
} from 'lucide-react';

/**
 * Icon mapping for CMDB component types
 * Used in diagram nodes, lists, and forms
 */
export const componentIcons: Record<string, LucideIcon> = {
  // Infrastructure & Compute
  server: Server,
  host: Server,
  vm: Server,
  virtual_machine: Server,
  compute: Server,
  cluster: ServerCluster,

  // Data Storage
  database: Database,
  datastore: Database,
  data_store: Database,
  data_lake: Database,
  storage: HardDrive,
  file_storage: Folder,
  blob_storage: Archive,

  // Message & Queue
  message_broker: Inbox,
  message_queue: Inbox,
  queue: Inbox,
  event_bus: Workflow,

  // Cloud & Platform
  cloud: Cloud,
  cloud_service: Cloud,
  cloud_platform: Cloud,
  platform: Layers,

  // Container & Orchestration
  kubernetes: Container,
  container: Container,
  container_orchestrator: Boxes,
  container_registry: Archive,

  // Applications & Services
  application: Box,
  app: Box,
  service: Package,
  microservice: Boxes,
  api: Workflow,
  web_service: Globe,
  saas: Cloud,

  // Network & Gateway
  network: Network,
  router: Router,
  gateway: Waypoints,
  dns: Globe,
  firewall: Shield,
  load_balancer: Activity,
  proxy: GitBranch,
  vpn: Lock,

  // Security & Identity
  security_zone: ShieldCheck,
  zone: ShieldCheck,
  secure_zone: ShieldCheck,
  identity_provider: Key,
  auth_service: Key,
  certificate: Lock,
  secret: Key,
  secret_manager: Key,

  // Monitoring & Management
  monitoring: Activity,
  monitoring_service: Activity,
  logging: FileText,
  logging_service: FileText,
  log_service: FileText,
  backup: Archive,
  backup_service: Archive,

  // Development & CI/CD
  repository: GitBranch,
  pipeline: Workflow,
  cicd_pipeline: Workflow,
  gitops: GitBranch,
  build_service: Settings,
  deployment: Zap,

  // Integration
  integration: GitBranch,
  integration_platform: GitBranch,
  webhook: Terminal,

  // Default fallback
  default: Box,
};

/**
 * Get icon component for a given component type
 * Falls back to default Box icon if type not found
 */
export const getComponentIcon = (type: string): LucideIcon => {
  const normalizedType = type?.toLowerCase().replace(/\s+/g, '_') || '';
  return componentIcons[normalizedType] || componentIcons.default;
};

/**
 * Get icon color based on component type category
 * Returns Avinor brand colors
 */
export const getComponentIconColor = (type: string): string => {
  const normalizedType = type?.toLowerCase().replace(/\s+/g, '_') || '';

  // Security & Identity - Purple (brand color)
  if (
    normalizedType.includes('security') ||
    normalizedType.includes('zone') ||
    normalizedType.includes('firewall') ||
    normalizedType.includes('auth') ||
    normalizedType.includes('identity') ||
    normalizedType.includes('vpn')
  ) {
    return '#2D3B45'; // Omni-Tek steel
  }

  // Data & Storage - Cyan
  if (
    normalizedType.includes('database') ||
    normalizedType.includes('storage') ||
    normalizedType.includes('data') ||
    normalizedType.includes('message') ||
    normalizedType.includes('queue')
  ) {
    return '#00D9FF'; // Omni-Tek cyan
  }

  // Network & Gateway - Ocher yellow
  if (
    normalizedType.includes('network') ||
    normalizedType.includes('router') ||
    normalizedType.includes('load_balancer') ||
    normalizedType.includes('proxy') ||
    normalizedType.includes('gateway') ||
    normalizedType.includes('dns')
  ) {
    return '#FFBB00'; // Avinor okergul
  }

  // Container & Orchestration - Dark purple
  if (
    normalizedType.includes('container') ||
    normalizedType.includes('kubernetes') ||
    normalizedType.includes('cluster')
  ) {
    return '#1A252E'; // Omni-Tek dark steel
  }

  // CI/CD & Pipeline - Dark steel
  if (
    normalizedType.includes('pipeline') ||
    normalizedType.includes('cicd') ||
    normalizedType.includes('registry') ||
    normalizedType.includes('gitops')
  ) {
    return '#1A252E'; // Omni-Tek dark steel
  }

  // Critical/Alerts - Coral
  if (
    normalizedType.includes('alert') ||
    normalizedType.includes('monitoring')
  ) {
    return '#FF5252'; // Omni-Tek coral
  }

  // Default - Dark steel
  return '#1A252E'; // Omni-Tek dark steel
};
